import {useState} from 'react';
import {useSnackbar} from 'notistack';
import {useLazyQuery} from '@apollo/client';
import {
  getReviewedValueFromField,
  notesOrder,
  useOnUpdateNoteMutation,
  serverToEditingNotes,
} from '../pages/HandoverNotesPage/HandoverNotesHelpers';
import {useReviewContext} from '../contexts/ReviewContext';
import {GET_HANDOVER_NOTES_QUERY_CURRENT_ONLY} from '../features/notes/notesQueries';
import {
  GetHandoverNotesCurrentOnly,
  GetHandoverNotesCurrentOnlyVariables,
} from '../schema-types/GetHandoverNotesCurrentOnly';

interface UseDummyHandoverNotesParams {
  /** Whether the form is in read-only mode */
  readOnly: boolean;
  /** Function to increment a refresh key to force component re-render */
  incrementRefreshKey: () => void;
}

/**
 * Custom hook for managing dummy data loading functionality for handover notes.
 *
 * This hook provides a standardized way to load dummy data into handover notes,
 * including loading states, error handling, and success notifications.
 * It automatically fetches the required handover notes data using the review context.
 *
 * @example
 * ```tsx
 * const MyComponent = ({ readOnly, incrementRefreshKey }) => {
 *   const { handleUseDummyData, isDummyDataLoading } = useDummyHandoverNotes({
 *     readOnly,
 *     incrementRefreshKey,
 *   });
 *
 *   return (
 *<Button
 *       onClick={handleUseDummyData}
 *       disabled={readOnly || isDummyDataLoading}
 *     >
 *       {isDummyDataLoading ? 'Loading...' : 'Use dummy data'}
 *     </Button>
 *   );
 * };
 * ```
 */
export const useDummyHandoverNotes = ({
  readOnly,
  incrementRefreshKey,
}: UseDummyHandoverNotesParams) => {
  const {enqueueSnackbar} = useSnackbar();
  const [isDummyDataLoading, setIsDummyDataLoading] = useState(false);

  // Get review context
  const {reviewId} = useReviewContext();

  // Query for handover notes data
  const [getHandoverData] = useLazyQuery<
    GetHandoverNotesCurrentOnly,
    GetHandoverNotesCurrentOnlyVariables
  >(GET_HANDOVER_NOTES_QUERY_CURRENT_ONLY, {
    variables: {
      keyId: 'review_id',
      valueId: reviewId,
      activeOnly: true,
    },
  });

  // Use the mutation hook with refetch enabled (true is the default)
  const {_onUpdateNote} = useOnUpdateNoteMutation(true);

  /**
   * Loads dummy data for all handover notes.
   * - Sets "Personal details" with styled text
   * - Marks other notes as "Reviewed but no requirements"
   * - Shows loading and success/error notifications
   * - Refetches data and triggers component re-render
   */
  const handleUseDummyData = async () => {
    if (readOnly || isDummyDataLoading) return;

    setIsDummyDataLoading(true);
    enqueueSnackbar('Loading dummy data...', {variant: 'info'});

    try {
      // Get notes from handover data
      const handoverData = await getHandoverData();
      const reviewDetails = handoverData.data?.reviewDetails;
      const notes = reviewDetails?.notes || [];

      // Create a map of note types to note data
      const noteMap = new Map();
      notes.forEach((note: any) => {
        if (note.name) {
          noteMap.set(note.name, note);
        }
      });

      // Prepare all note updates
      const noteUpdates = notesOrder
        .map((noteType) => {
          const note = noteMap.get(noteType);
          if (!note) return null;

          if (noteType === 'Attitude to risk') {
            return null;
          }

          const body =
            noteType === 'Personal details'
              ? '<bold>styled text test</bold>'
              : 'Reviewed but no requirements';

          const reviewed = noteType !== 'Personal details';

          // Create a temporary note object for getReviewedValueFromField
          const tempNote = {
            ...serverToEditingNotes(note),
            reviewed,
            body: noteType === 'Personal details' ? body : '',
            markupBody: noteType === 'Personal details' ? body : null,
          };

          return _onUpdateNote({
            variables: {
              noteId: note.id,
              name: note.name,
              body: reviewed ? '' : body,
              markupBody: reviewed ? null : noteType === 'Personal details' ? body : null,
              reviewed: reviewed
                ? 'Reviewed but no requirements'
                : getReviewedValueFromField(tempNote),
            },
          });
        })
        .filter(Boolean);

      // Execute all updates in parallel
      await Promise.all(noteUpdates);

      // The mutations will automatically refetch the data due to refetchQueries
      // Increment the refresh key to force component re-render
      incrementRefreshKey();

      enqueueSnackbar('Dummy data loaded successfully!', {variant: 'success'});
    } catch (error) {
      enqueueSnackbar('Failed to load dummy data', {variant: 'error'});
    } finally {
      setIsDummyDataLoading(false);
    }
  };

  return {
    /** Function to trigger dummy data loading */
    handleUseDummyData,
    /** Boolean indicating if dummy data is currently being loaded */
    isDummyDataLoading,
  };
};
